import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Send, History } from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { useAIView } from '@/context/AIViewContext';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { Button } from '@/components/ui/button';

const PLACEHOLDER_PRIMARY = 'Ask Inny anything...';
const PLACEHOLDER_SHORTCUT = 'Ctrl+I to focus';
const DISPLAY_DURATION = 8000; // Show each text for 8 seconds
const FADE_DURATION = 1200; // 1.2s fade transition

export const AIFloatingBubble: React.FC = () => {
  const { openPreferred, openPreferredWithHistory, setPendingMessage, setPendingNewChat, viewMode, isBubbleCollapsed } = useAIView();
  const isMobile = useIsMobile();
  const [input, setInput] = useState('');
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [currentPlaceholder, setCurrentPlaceholder] = useState<'primary' | 'shortcut'>('primary');
  const [placeholderVisible, setPlaceholderVisible] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  // Combined state for hover OR focus
  const isActive = isHovered || isFocused;

  // Sequential fade: fade out -> switch text -> fade in
  useEffect(() => {
    // Reset when focused or has input
    if (isFocused || input) {
      setCurrentPlaceholder('primary');
      setPlaceholderVisible(true);
      return;
    }

    const startTransition = () => {
      // Phase 1: Fade out
      setPlaceholderVisible(false);

      // Phase 2: After fade out, switch text and fade in
      setTimeout(() => {
        setCurrentPlaceholder(prev => prev === 'primary' ? 'shortcut' : 'primary');
        // Small delay to ensure text changed before fading in
        requestAnimationFrame(() => {
          setPlaceholderVisible(true);
        });
      }, FADE_DURATION);
    };

    // Start the cycle after display duration
    const interval = setInterval(startTransition, DISPLAY_DURATION);

    return () => clearInterval(interval);
  }, [isFocused, input]);

  // Global keyboard shortcut: Ctrl+I to focus
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'i') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Reset states when component becomes visible again (after sidebar closes)
  useEffect(() => {
    if (viewMode === 'hidden') {
      // Reset focus state when bubble becomes visible again
      setIsFocused(false);
      setIsHovered(false);
    }
  }, [viewMode]);

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedInput = input.trim();
    if (!trimmedInput) return;

    setPendingMessage(trimmedInput);
    setPendingNewChat(true); // Always start a new conversation from bubble
    openPreferred();
    setInput('');
    // Blur the input when submitting
    inputRef.current?.blur();
  }, [input, setPendingMessage, setPendingNewChat, openPreferred]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const handleOpenHistory = useCallback(() => {
    openPreferredWithHistory();
    inputRef.current?.blur();
  }, [openPreferredWithHistory]);

  // Don't render on mobile
  if (isMobile) {
    return null;
  }

  // Bubble should be hidden when collapsed OR when sidebar/floating is open
  const isHidden = isBubbleCollapsed || viewMode !== 'hidden';

  return (
    <div
      data-onboarding-id="ai-chat-trigger"
      className={cn(
        "fixed bottom-4 left-1/2 -translate-x-1/2 z-40",
        "transition-all duration-300 ease-out",
        isHidden && 'pointer-events-none'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={cn(
          'transition-all duration-500 ease-out',
          // Hover scale effect (but not when focused)
          !isFocused && isHovered && 'scale-105',
        )}
      >
        <form onSubmit={handleSubmit} className="relative">
          <label
            className={cn(
              'relative flex items-center rounded-full p-2 overflow-hidden',
              'backdrop-blur-sm shadow-lg dark:shadow-black/40',
              'border transition-all duration-300 ease-out',
              // Width: hidden (0) -> normal (240) -> focused (480)
              isHidden ? 'w-0 opacity-0 p-0 border-0' : (isFocused ? 'w-[480px]' : 'w-[240px]'),
              // Transparency based on active state - use muted in dark mode for better contrast
              isActive
                ? 'border-border bg-popover dark:bg-muted dark:border-border'
                : 'border-border/50 bg-popover/40 dark:bg-muted/80 dark:border-border/70',
            )}
          >
            {/* TagInn Logo */}
            <div className="shrink-0 ml-1 mr-2">
              <img
                src="/media/tag-innovation-logo.png"
                alt="Inny"
                className={cn(
                  'h-5 w-5 transition-opacity',
                  isActive ? 'opacity-100' : 'opacity-70'
                )}
              />
            </div>

            {/* Input with animated placeholder */}
            <div className="relative flex-1 h-8">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                className={cn(
                  'h-8 w-full bg-transparent text-sm text-foreground',
                  'focus:outline-none',
                  // Extra padding for buttons when focused
                  isFocused ? 'pr-20' : 'pr-10'
                )}
                aria-label="Message Inny (Ctrl+I to focus)"
              />
              {/* Custom animated placeholder - sequential fade */}
              <div
                className={cn(
                  "absolute inset-0 flex items-center pointer-events-none text-sm text-muted-foreground",
                  "transition-opacity duration-200",
                  (input || isFocused) ? 'opacity-0' : 'opacity-100'
                )}
              >
                <span
                  className={cn(
                    'transition-opacity ease-in-out',
                    placeholderVisible ? 'opacity-100' : 'opacity-0'
                  )}
                  style={{ transitionDuration: `${FADE_DURATION}ms` }}
                >
                  {currentPlaceholder === 'primary' ? PLACEHOLDER_PRIMARY : PLACEHOLDER_SHORTCUT}
                </span>
              </div>
            </div>

            {/* History button - only visible when focused */}
            <Button
              type="button"
              onMouseDown={(e) => {
                // Prevent blur before click registers
                e.preventDefault();
                handleOpenHistory();
              }}
              className={cn(
                'absolute right-12 top-1/2 -translate-y-1/2',
                'h-7 w-7 flex items-center justify-center',
                'rounded-full',
                'text-muted-foreground hover:text-foreground hover:bg-muted/50',
                'transition-all duration-200',
                // Fade in/out based on focus
                isFocused ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none',
              )}
              aria-label="Open chat history"
              tabIndex={-1}
            >
              <History className="h-4 w-4" />
            </Button>

            {/* Send button - positioned inside */}
            <Button
              type="submit"
              disabled={!input.trim()}
              className={cn(
                'absolute right-2 top-1/2 -translate-y-1/2',
                'h-8 w-8 flex items-center justify-center',
                'rounded-full',
                'bg-primary text-primary-foreground',
                'disabled:opacity-40 disabled:cursor-not-allowed',
                'hover:opacity-80 transition-opacity',
              )}
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </Button>
          </label>
        </form>
      </div>
    </div>
  );
};

export default AIFloatingBubble;
